import { useState } from "react"
import { AcademicCapIcon } from "@heroicons/react/24/outline"
import { Education } from "./types"
import { Section, Item, EmptyState } from "./ui"
import { Modal } from "../../components/modal/Modal"

export const EducationBlock = ({
  items,
  onAdd,
  onDelete,
}: {
  items: Education[]
  onAdd: (data: Omit<Education, "id">) => void
  onDelete: (id: string) => void
}) => {
  const [open, setOpen] = useState(false)

  const [institution, setInstitution] = useState("")
  const [degree, setDegree] = useState("")
  const [startYear, setStartYear] = useState<number | "">("")
  const [endYear, setEndYear] = useState<number | "">("")
  const [current, setCurrent] = useState(false)

  const resetForm = () => {
    setInstitution("")
    setDegree("")
    setStartYear("")
    setEndYear("")
    setCurrent(false)
  }

  const handleSave = () => {
    if (!institution.trim() || !degree.trim() || !startYear) return

    onAdd({
      institution,
      degree,
      start_year: Number(startYear),
      ...(current ? {} : endYear ? { end_year: Number(endYear) } : {}),
    })

    resetForm()
    setOpen(false)
  }

  return (
    <>
      <Section
        id="education"
        title="Education"
        icon={AcademicCapIcon}
        iconColor="text-violet-500 bg-violet-50"
        description="Your academic background and qualifications"
        onAdd={() => setOpen(true)}
      >
        {!items.length && (
          <EmptyState
            icon={AcademicCapIcon}
            title="No education added yet"
            subtitle="Add your education to complete your profile"
            ctaLabel="Add education"
            onAdd={() => setOpen(true)}
          />
        )}

        {items.map((e) => (
          <Item
            key={e.id}
            title={e.institution}
            subtitle={formatEducation(e)}
            onDelete={() => onDelete(e.id)}
          />
        ))}
      </Section>

      {/* MODAL */}
      <Modal
        open={open}
        onClose={() => {
          resetForm()
          setOpen(false)
        }}
        title="Add education"
      >
        <div className="space-y-3">
          <input
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
            placeholder="Institution"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          <input
            value={degree}
            onChange={(e) => setDegree(e.target.value)}
            placeholder="Degree / Field of study"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          <div className="flex gap-2">
            <input
              type="number"
              min={1960}
              max={2030}
              value={startYear}
              onChange={(e) => setStartYear(e.target.value === "" ? "" : Number(e.target.value))}
              placeholder="Start year"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            {!current && (
              <input
                type="number"
                min={1960}
                max={2030}
                value={endYear}
                onChange={(e) => setEndYear(e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="End year"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={current}
              onChange={(e) => {
                setCurrent(e.target.checked)
                if (e.target.checked) setEndYear("")
              }}
              className="rounded border-gray-300 text-blue-600"
            />
            I currently study here
          </label>

          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => { resetForm(); setOpen(false) }}
              className="border border-gray-200 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition"
            >
              Cancel
            </button>

            <button
              onClick={handleSave}
              disabled={!institution || !degree || !startYear}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50 hover:bg-blue-700 transition"
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

/* ===================== HELPERS ===================== */

const formatEducation = (e: Education) => {
  const period = e.end_year
    ? `${e.start_year} – ${e.end_year}`
    : `${e.start_year} – Present`

  return `${e.degree} · ${period}`
}
